import React, { useEffect, useMemo, useState, useCallback } from "react";

import {
  Mail,
  Eye,
  FileText,
  Building2,
  Users,
  AlertTriangle,
  X,
  LogIn,
} from "lucide-react";

import Button from "../../../Components/Button";
import CommonModal from "../../../Components/Modal";
import { useAppDispatch, useAppSelector } from "../../../Store/Hooks";
import {
  fetchAssignmentsOverview,
  AssignmentsOverviewItem,
  sendPrepActivationEmail,
  clearSendPrepActivationEmail,
} from "../../../Store/Reducers/OpsSlice";
import { toast } from "../../../Utils/Helper/toast";
import AssignmentDetailsModal from "../AssignmentDetailsModal";

import "./style.scss";

// Types
type FlatRecord = {
  id: string;
  executive: {
    name: string;
    email: string;
  };
  account: {
    name: string;
    customer_id?: string;
  };
  sponsorType: string | null;
  batchName: string;
  activatedDate: string;
  activatedDateKey: string;
  loginStatus: any;
  batchInfo: any;
  assignmentRequestId: string | null;
  assignmentRequestStatus: string | null;
  fullItem: AssignmentsOverviewItem;
  uploadType: string;
  accountCount: number;
};

type DateGroup = {
  date: string;
  dateFormatted: string;
  records: FlatRecord[];
  pendingLoginsCount: number;
  executives: Array<{
    executiveKey: string;
    executiveData: {
      name: string;
      email: string;
    };
    records: FlatRecord[];
  }>;
};

// Helper Functions
const formatDateHeader = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString("en-US", {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};

const formatActivatedTime = (
  dateString: string
): { date: string; time: string } => {
  if (!dateString) return { date: "-", time: "-" };

  try {
    const date = new Date(dateString);
    const formattedDate = date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
    const formattedTime = date.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
    const tzOffset = date.getTimezoneOffset();
    const tzHours = Math.floor(Math.abs(tzOffset) / 60);
    const tzMinutes = Math.abs(tzOffset) % 60;
    const tzSign = tzOffset <= 0 ? "+" : "-";
    const tzString = `GMT${tzSign}${tzHours}:${tzMinutes.toString().padStart(2, "0")}`;

    return {
      date: formattedDate,
      time: `${formattedTime} ${tzString}`,
    };
  } catch {
    return { date: dateString, time: "" };
  }
};

const getSponsorTypeLabel = (type?: string | null) => {
  if (!type) return "Sponsor";
  const t = type.toLowerCase();
  if (t === "primary") return "Primary Sponsor";
  if (t === "secondary") return "Secondary Sponsor";
  return `${type.charAt(0).toUpperCase()}${type.slice(1)} Sponsor`;
};

// Helper function to get date threshold based on filter
const getDateThreshold = (timeFilter: string): Date | null => {
  if (timeFilter === "all") return null;

  const now = new Date();
  const threshold = new Date(now);

  switch (timeFilter) {
    case "7-days":
      threshold.setDate(now.getDate() - 7);
      break;
    case "30-days":
      threshold.setDate(now.getDate() - 30);
      break;
    case "90-days":
      threshold.setDate(now.getDate() - 90);
      break;
    default:
      return null;
  }

  return threshold;
};

interface IncompleteActivationsContainerProps {
  timeFilter?: string;
}

const IncompleteActivationsContainer: React.FC<
  IncompleteActivationsContainerProps
> = ({ timeFilter = "all" }) => {
  const dispatch = useAppDispatch();
  const {
    data: assignmentsOverviewData,
    loading,
    error,
  } = useAppSelector(state => state.ops.assignmentsOverview);
  const {
    loading: resendEmailLoading,
    success: resendEmailSuccess,
    error: resendEmailError,
  } = useAppSelector(state => state.ops.sendPrepActivationEmail);

  const [showDetails, setShowDetails] = useState(false);
  const [selectedRow, setSelectedRow] = useState<any | null>(null);
  const [showResendEmailModal, setShowResendEmailModal] = useState(false);
  const [selectedEmailRecord, setSelectedEmailRecord] = useState<any | null>(
    null
  );

  useEffect(() => {
    dispatch(fetchAssignmentsOverview("not_logged"));
  }, [dispatch]);

  // Group activations by activation date, then by executive
  const groupedActivations = useMemo((): DateGroup[] => {
    if (!assignmentsOverviewData?.items) return [];

    const dateThreshold = getDateThreshold(timeFilter);
    const flatRecords: FlatRecord[] = assignmentsOverviewData.items
      .filter((item: AssignmentsOverviewItem) => {
        // Filter by time period if specified
        if (dateThreshold && item.activated_date) {
          const activatedDate = new Date(item.activated_date);
          return activatedDate >= dateThreshold;
        }
        return true;
      })
      .map((item: AssignmentsOverviewItem) => {
        // Determine batch name from batch or assignment_request
        const assignmentRequest = (item as any).assignment_request;
        let batchName = "No Assignment";
        if (item.type === "batch" && item.batch) {
          batchName = item.batch.batch_name;
        } else if (assignmentRequest?.batch_name) {
          batchName = assignmentRequest.batch_name;
        } else if (item.type === "assignment_request") {
          batchName = `Assignment: ${item.account.name}`;
        }

        const activatedDate = item.activated_date;
        const activatedDateKey = activatedDate
          ? new Date(activatedDate).toDateString()
          : new Date().toDateString();

        return {
          id: item.id,
          executive: {
            name: item.executive.name,
            email: item.executive.email,
            userId: item.executive.user_id,
          },
          account: {
            name: item.account.name || "No Assignment",
            customer_id: item.account.customer_id,
          },
          sponsorType:
            (item.executive as any)?.repso_type ||
            (item.executive as any)?.type ||
            null,
          batchName,
          activatedDate,
          activatedDateKey,
          loginStatus: item.login_status,
          batchInfo: item.batch,
          assignmentRequestId: item.assignment_request_id,
          assignmentRequestStatus: item.assignment_request_status,
          fullItem: item,
          uploadType:
            (item as any).assignment_source ||
            item.batch?.upload_type ||
            "composite",
          accountCount: 1,
        };
      });

    // Group by activation date first, then by executive
    const dateGroups = new Map<string, Map<string, FlatRecord[]>>();

    flatRecords.forEach(record => {
      const dateKey = record.activatedDateKey;
      const executiveKey =
        record.executive.email || record.executive.name || "unknown";

      if (!dateGroups.has(dateKey)) {
        dateGroups.set(dateKey, new Map());
      }

      const executiveMap = dateGroups.get(dateKey)!;
      if (!executiveMap.has(executiveKey)) {
        executiveMap.set(executiveKey, []);
      }

      executiveMap.get(executiveKey)!.push(record);
    });

    // Convert to sorted array format
    const sortedGroups: DateGroup[] = [];

    dateGroups.forEach((executiveMap, dateKey) => {
      const allRecords: FlatRecord[] = [];

      executiveMap.forEach(records => {
        // Sort records by activation time within each executive group
        records.sort((a, b) => {
          const timeA = a.activatedDate
            ? new Date(a.activatedDate).getTime()
            : 0;
          const timeB = b.activatedDate
            ? new Date(b.activatedDate).getTime()
            : 0;
          return timeB - timeA;
        });

        allRecords.push(...records);
      });

      // Sort executives by first record's activation time
      const executives = Array.from(executiveMap.entries()).sort((a, b) => {
        const timeA = a[1][0]?.activatedDate
          ? new Date(a[1][0].activatedDate).getTime()
          : 0;
        const timeB = b[1][0]?.activatedDate
          ? new Date(b[1][0].activatedDate).getTime()
          : 0;
        return timeB - timeA;
      });

      // Count pending logins (not logged in)
      const pendingLoginsCount = allRecords.filter(
        record => record.loginStatus?.login_status === "Not Logged In"
      ).length;

      sortedGroups.push({
        date: dateKey,
        dateFormatted: formatDateHeader(dateKey),
        records: allRecords,
        pendingLoginsCount,
        executives: executives.map(([executiveKey, records]) => ({
          executiveKey,
          executiveData: records[0]?.executive,
          records,
        })),
      });
    });

    // Sort date groups by date (most recent first)
    sortedGroups.sort((a, b) => {
      const dateA = new Date(a.date).getTime();
      const dateB = new Date(b.date).getTime();
      return dateB - dateA;
    });

    return sortedGroups;
  }, [assignmentsOverviewData, timeFilter]);

  const openDetails = useCallback((row: any) => {
    setSelectedRow(row);
    setShowDetails(true);
  }, []);

  const handleSendEmail = useCallback((row: any) => {
    setSelectedEmailRecord(row);
    setShowResendEmailModal(true);
  }, []);

  const closeResendEmailModal = useCallback(() => {
    setShowResendEmailModal(false);
    setSelectedEmailRecord(null);
  }, []);

  // Handle resend email success
  useEffect(() => {
    if (resendEmailSuccess && selectedEmailRecord) {
      toast.success(
        `Activation email has been resent to ${selectedEmailRecord?.executive?.email || "the executive"}`
      );
      closeResendEmailModal();
      dispatch(clearSendPrepActivationEmail());
    }
  }, [
    resendEmailSuccess,
    selectedEmailRecord,
    dispatch,
    closeResendEmailModal,
  ]);

  // Handle resend email error
  useEffect(() => {
    if (resendEmailError) {
      toast.error(resendEmailError);
      dispatch(clearSendPrepActivationEmail());
    }
  }, [resendEmailError, dispatch]);

  const handleConfirmResendEmail = useCallback(() => {
    if (!selectedEmailRecord) return;

    // Get the user ID from the record
    const userId = selectedEmailRecord?.executive?.userId;
    if (!userId) {
      toast.error("Unable to find user ID. Please try again.");
      return;
    }

    // Dispatch Redux action to send prep activation email
    dispatch(sendPrepActivationEmail(userId) as any);
  }, [selectedEmailRecord, dispatch]);

  const closeDetails = useCallback(() => {
    setShowDetails(false);
    setSelectedRow(null);
  }, []);

  // Loading state
  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] py-8 mt-4">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto"></div>
        <p className="text-gray-600 mt-2">Loading incomplete activations...</p>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] py-8 mt-4 text-center">
        <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mb-4">
          <AlertTriangle className="w-8 h-8 text-red-600" />
        </div>
        <p className="text-base font-medium text-red-600 mb-2">
          Error loading incomplete activations
        </p>
        <p className="text-sm text-red-500">{error}</p>
      </div>
    );
  }

  // Empty state
  if (!groupedActivations || groupedActivations.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] py-8 mt-4">
        <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mb-4">
          <FileText className="w-8 h-8 text-gray-400" />
        </div>
        <p className="text-base font-medium text-gray-600">
          No incomplete activations found.
        </p>
      </div>
    );
  }

  return (
    <div className="incomplete-activations">
      {/* Table Container with Fixed Header */}
      {groupedActivations.length > 0 && (
        <div className="table-container-wrapper">
          {/* Fixed Table Header */}
          <div className="sticky top-0 z-20 bg-white shadow overflow-hidden">
            <div className="overflow-x-auto">
              <div className="grid grid-cols-[380px_1.1fr_180px_180px_1.5fr] gap-4 px-3 py-3 bg-gray-50 border-gray-200 text-xs font-medium text-gray-500 uppercase tracking-wider">
                <div className="flex items-center">SOURCE</div>
                <div className="flex items-center">ACCOUNT</div>
                <div className="flex items-center">ACTIVATED AT</div>
                <div className="flex items-center">SPONSOR TYPE</div>
                <div className="flex items-center justify-end">ACTIONS</div>
              </div>
            </div>
          </div>

          {/* Scrollable Content */}
          <div className="table-content-scrollable">
            {/* Date Groups */}
            {groupedActivations.map(dateGroup => {
              return (
                <div key={dateGroup.date} className={"mt-4"}>
                  {/* Custom Table Structure */}
                  <div className={"bg-white shadow overflow-hidden mb-2"}>
                    {/* Date Header - Combined with table */}
                    <div className="flex items-center justify-between px-3 py-2.5 bg-gray-50 border-b border-gray-200">
                      <div className="flex items-center gap-2">
                        <AlertTriangle className="w-4 h-4 text-gray-500" />
                        <h3 className="text-sm font-semibold text-gray-700">
                          Activated {dateGroup.dateFormatted}
                        </h3>
                      </div>
                      <div className="flex items-center gap-3">
                        {dateGroup.pendingLoginsCount > 0 && (
                          <div className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-teal-100 text-teal-800">
                            {dateGroup.pendingLoginsCount}{" "}
                            {dateGroup.pendingLoginsCount === 1
                              ? "pending login"
                              : "pending logins"}
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="overflow-x-auto">
                      {/* Table Body */}
                      <div className="divide-y divide-gray-200">
                        {dateGroup.executives.map(
                          (executiveGroup, execIndex) => {
                            const execRecords = executiveGroup.records;
                            // Count accounts that are not logged in for this executive
                            const notLoggedInCount = execRecords.filter(
                              r =>
                                r.loginStatus?.login_status === "Not Logged In"
                            ).length;
                            const hasNotLoggedIn = notLoggedInCount > 0;

                            return (
                              <div key={`${dateGroup.date}-${execIndex}`}>
                                {/* Executive Header Row */}
                                <div className="grid grid-cols-[380px_1.1fr_180px_180px_1.5fr] gap-4 px-3 py-2 border-b border-gray-100">
                                  <div className="flex items-center gap-3">
                                    <div className="flex-shrink-0">
                                      <div className="flex-shrink-0 h-8 w-8">
                                        <div className="h-8 w-8 rounded-full bg-indigo-100 flex items-center justify-center">
                                          <Users className="h-4 w-4 text-indigo-600" />
                                        </div>
                                      </div>
                                    </div>
                                    <div className="leading-tight min-w-0 flex-1">
                                      <div className="font-semibold text-gray-900 text-sm mb-0.5">
                                        {executiveGroup.executiveData?.name ||
                                          "N/A"}
                                      </div>
                                      <div className="text-xs text-gray-500 flex gap-1 flex-wrap">
                                        <span className="truncate">
                                          {executiveGroup.executiveData
                                            ?.email || "N/A"}
                                        </span>
                                      </div>
                                    </div>
                                  </div>
                                  <div className="flex items-center"></div>
                                  <div className="flex items-center"></div>
                                  <div className="flex items-center"></div>
                                  <div className="flex items-center justify-end gap-2 text-xs text-gray-600">
                                    {hasNotLoggedIn && (
                                      <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                        <div className="flex items-center gap-0.5">
                                          <LogIn className="h-3 w-3 mr-1" />
                                        </div>
                                        <span>Not Logged In</span>
                                      </span>
                                    )}
                                    <span>
                                      {execRecords.length}{" "}
                                      {execRecords.length === 1
                                        ? "account"
                                        : "accounts"}
                                    </span>
                                  </div>
                                </div>

                                {/* Activation Data Rows */}
                                {execRecords.map(record => {
                                  const activatedTime = formatActivatedTime(
                                    record.activatedDate
                                  );
                                  return (
                                    <div
                                      key={record.id}
                                      className="grid grid-cols-[380px_1.1fr_180px_180px_1.5fr] gap-4 px-3 py-2 hover:bg-gray-50/70 transition-colors border-t border-gray-100"
                                    >
                                      {/* SOURCE */}
                                      <div className="flex items-center">
                                        <span
                                          className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${record.uploadType === "composite" ? "bg-blue-100 text-blue-800" : "bg-purple-100 text-purple-800"}`}
                                        >
                                          <FileText className="w-3 h-3 mr-1.5 flex-shrink-0" />
                                          {record.uploadType === "in_app"
                                            ? "In App"
                                            : "Composite"}
                                        </span>
                                      </div>

                                      {/* ACCOUNT */}
                                      <div className="flex items-center gap-2.5 min-w-0">
                                        <div className="w-8 h-8 rounded bg-green-100 flex items-center justify-center flex-shrink-0">
                                          <Building2 className="w-4 h-4 text-green-700" />
                                        </div>
                                        <div className="min-w-0 flex-1">
                                          <div className="flex items-center gap-2 mb-0.5">
                                            <span className="text-sm text-gray-900 font-medium">
                                              {record.account.name}
                                            </span>
                                          </div>
                                        </div>
                                      </div>

                                      {/* ACTIVATED AT */}
                                      <div className="flex flex-col justify-center">
                                        <div className="text-sm text-gray-600 leading-tight">
                                          {activatedTime.date}
                                        </div>
                                        <div className="text-xs text-gray-500 mt-0.5 leading-tight">
                                          {activatedTime.time}
                                        </div>
                                      </div>

                                      {/* SPONSOR TYPE */}
                                      <div className="flex items-center">
                                        <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-medium text-gray-700">
                                          {getSponsorTypeLabel(
                                            record.sponsorType
                                          )}
                                        </span>
                                      </div>

                                      {/* ACTIONS */}
                                      <div className="flex items-center justify-end gap-2">
                                        <Button
                                          variant="clear"
                                          onClick={() =>
                                            handleSendEmail(record)
                                          }
                                          title="Resend"
                                          className="bg-orange-600 hover:bg-orange-700 text-white border-orange-600 flex items-center gap-1.5 px-3 py-1 rounded"
                                        >
                                          <Mail size={16} />
                                          <span className="text-xs font-normal">
                                            Resend
                                          </span>
                                        </Button>
                                        <Button
                                          variant="clear"
                                          onClick={() => openDetails(record)}
                                          title="Details"
                                          className="bg-blue-600 hover:bg-blue-700 text-white border-blue-600 flex items-center gap-1.5 px-3 py-1 rounded"
                                        >
                                          <Eye size={16} />
                                          <span className="text-xs font-normal">
                                            Details
                                          </span>
                                        </Button>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            );
                          }
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Resend Email Confirmation Modal */}
      <CommonModal
        isOpen={showResendEmailModal}
        toggle={closeResendEmailModal}
        title=""
        size="3xl"
        centered
        showHeader={false}
      >
        <div className="p-2">
          {/* Custom Header with Icon */}
          <div className="flex items-center justify-between mb-4 pb-4 border-b border-gray-200">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center">
                <Mail className="w-5 h-5 text-orange-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">
                Confirm Resend Email
              </h3>
            </div>
            <button
              onClick={closeResendEmailModal}
              className="text-gray-400 hover:text-gray-600 transition-colors"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Description Text */}
          <p className="text-sm text-gray-700 mb-6">
            This will resend the activation email to the selected
            executive&apos;s email address.
          </p>

          {/* Information Table */}
          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden mb-6">
            {/* Table Header */}
            <div className="grid grid-cols-3 gap-4 px-4 py-3 bg-gray-50 border-b border-gray-200">
              <div className="text-xs font-semibold text-gray-700 uppercase tracking-wider">
                EXECUTIVE
              </div>
              <div className="text-xs font-semibold text-gray-700 uppercase tracking-wider">
                EMAIL
              </div>
              <div className="text-xs font-semibold text-gray-700 uppercase tracking-wider">
                ACCOUNT
              </div>
            </div>

            {/* Table Body */}
            <div className="px-4 py-3">
              <div className="grid grid-cols-3 gap-4 items-center">
                {/* Executive Column */}
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0">
                    <Users className="w-4 h-4 text-purple-600" />
                  </div>
                  <span className="text-sm font-medium text-gray-900">
                    {selectedEmailRecord?.executive?.name || "N/A"}
                  </span>
                </div>

                {/* Email Column */}
                <div>
                  <span className="text-sm text-gray-500">
                    {selectedEmailRecord?.executive?.email || "N/A"}
                  </span>
                </div>

                {/* Account Column */}
                <div className="flex items-center gap-2">
                  <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                    <Building2 className="w-4 h-4 text-green-600" />
                  </div>
                  <span className="text-sm text-gray-900">
                    {selectedEmailRecord?.account?.name || "N/A"}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200">
            <Button
              variant="secondary"
              onClick={closeResendEmailModal}
              disabled={resendEmailLoading}
            >
              Go Back
            </Button>
            <Button
              variant="primary"
              onClick={handleConfirmResendEmail}
              loading={resendEmailLoading}
              className="!bg-orange-600 hover:!bg-orange-700"
            >
              Resend Email
            </Button>
          </div>
        </div>
      </CommonModal>

      <AssignmentDetailsModal
        isOpen={showDetails}
        onClose={closeDetails}
        selectedRow={selectedRow}
      />
    </div>
  );
};

export default IncompleteActivationsContainer;
